import { FunctionComponent, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { styles } from '../../../styles/themes/style'
import { useBillingStore } from '../../../stores/billing/useBillingStore'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import { useBillingHandlers } from '../../../services/actions/billing/useBillingHandlers'
import { useExternalHandlers } from '../../../services/actions/navigation/useExternalHandlers'
import { AppRoutes } from '../../../interfaces/store/appStore'
import { IBillingPlanV2, IPrice } from '../../../interfaces/models/billingPlan'
import { getActivePrice } from '../../../utils/pricingUtils'
import { calculateSaveAmount } from '../components/CycleToggle'
import CurrencySelect from '../components/CurrencySelect'
import IntervalSwitch from './components/IntervalSwitch'
import GrowthPlanCard from './components/GrowthPlanCard'
import DoneForYouCard from './components/DoneForYouCard'
import EnterpriseSection from './components/EnterpriseSection'
import { PLAN_CARDS_SIDE_BY_SIDE, pricingContentWidth } from './components/PlanCard'
import { GROWTH_SOURCE_PLAN_NAME } from './pricingV2'

interface IPlansAndPricingSectionV2Props {
    hideTitle?: boolean
}

const PlansAndPricingSectionV2: FunctionComponent<IPlansAndPricingSectionV2Props> = ({ hideTitle }) => {
    const { store: { billingPlans } } = useBillingStore()
    const { store: { subscription, trial } } = useAuthStore()
    const { setSelectedPlanAndPriceHandler } = useBillingHandlers()
    const { openDoneForYouMeeting, openEnterpriseMeeting } = useExternalHandlers()
    const navigate = useNavigate()

    const hasActiveSubscription = subscription?.isValid && !trial && !(subscription?.cancelDate && subscription?.isValid)

    // Active subscribers can't switch currency, so show prices in the one they already pay in
    const [currency, setCurrency] = useState<string>(hasActiveSubscription && subscription?.currency ? subscription.currency : 'usd')
    const [selectedCycle, setSelectedCycle] = useState<string>('year')

    const growthPlan: IBillingPlanV2 | undefined = billingPlans.find((plan: IBillingPlanV2) => plan.name === GROWTH_SOURCE_PLAN_NAME)
    const monthlyPrice = growthPlan && getActivePrice(growthPlan.prices, 'month', currency)
    const yearlyPrice = growthPlan && getActivePrice(growthPlan.prices, 'year', currency)
    const selectedPrice = selectedCycle === 'year' ? yearlyPrice : monthlyPrice
    const savePercent = Math.round(calculateSaveAmount(monthlyPrice, yearlyPrice) / 100)
    const saveLabel = savePercent > 0 ? `Save ${savePercent}%` : undefined
    const yearlySaving = monthlyPrice && yearlyPrice ? monthlyPrice.price * 12 - yearlyPrice.price : 0

    const userPrice: IPrice | undefined = growthPlan?.prices.find((price: IPrice) => price.priceId === subscription?.priceId && price.productId === subscription?.productId)
    const isGrowthSubscriber = hasActiveSubscription && !!userPrice && userPrice.currency === currency
    const isCurrentPlan = isGrowthSubscriber && userPrice?.interval === selectedCycle

    const onBuy = () => {
        if (!growthPlan || isCurrentPlan) return
        const preSelectedPrice = setSelectedPlanAndPriceHandler(growthPlan, selectedCycle, currency)
        navigate(AppRoutes.BILLING_PLAN, { state: {
            changeSeats: isGrowthSubscriber,
            preSelectedBillingPlan: growthPlan,
            preSelectedPrice,
            // Yearly picked here can't be switched back to Monthly in the next step
            yearlyOnly: selectedCycle === 'year'
        }})
    }

    return (
        <Container>
            <Header>
                {!hideTitle && <div>
                    <Title>LeadDelta Pricing</Title>
                    <Subtitle>Start free for 7 days. Add seats and data credits as you grow.</Subtitle>
                </div>}
                {!hasActiveSubscription && <CurrencyWrapper><CurrencySelect onChangeHandler={setCurrency} /></CurrencyWrapper>}
                <IntervalSwitch selectedInterval={selectedCycle} saveLabel={saveLabel} onChange={setSelectedCycle} />
            </Header>
            <PlansGrid>
                {selectedPrice && <GrowthPlanCard
                    selectedPrice={selectedPrice}
                    yearlySaving={yearlySaving}
                    saveLabel={saveLabel}
                    isCurrentPlan={isCurrentPlan}
                    onBuy={onBuy}
                />}
                <DoneForYouCard onBookCall={openDoneForYouMeeting} />
            </PlansGrid>
            <EnterpriseSection onBookCall={openEnterpriseMeeting} />
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 12px 24px 24px 24px;
    overflow-y: auto;
    border-radius: 4px;
    background-color: ${styles.colors.white};
    font-family: NunitoSans;

    /* This column scrolls inside a fixed height; without this the overflow-hidden Enterprise block shrinks to fit it */
    & > * {
        flex-shrink: 0;
    }

    @media only screen and (max-width: 599px) {
        padding: 16px;
    }
`
const Header = styled.section`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    text-align: center;
`
const Title = styled.h1`
    margin: 0;
    font-family: NunitoSans;
    font-size: 26px;
    font-weight: 700;
    line-height: 32px;
    color: ${styles.colors.black600};
`
const Subtitle = styled.p`
    margin: 2px 0 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black400};
`
const CurrencyWrapper = styled.div`
    align-self: flex-end;

    @media only screen and (min-width: 768px) {
        position: absolute;
        top: 0;
        right: 0;
    }

    .actions-dropdown + div {
        min-width: 138px;
    }
`
const PlansGrid = styled.div`
    ${pricingContentWidth}
    padding-top: 14px;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;

    @media only screen and (min-width: ${PLAN_CARDS_SIDE_BY_SIDE}px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-rows: auto 1fr auto;
        column-gap: 24px;
        row-gap: 22px;
    }
`

export default PlansAndPricingSectionV2
